// pages/DashboardPage.tsx
import { Users, FileText, Star, HelpCircle, Briefcase, Eye, EyeOff, TrendingUp, Calendar } from "lucide-react";
import usePosts from "../hooks/features/posts/usePosts";
import useReviews from "../hooks/features/reviews/useReviews";
import useFaqs from "../hooks/features/faqs/useFaqs";
import useJobOffers from "../hooks/features/job-offers/useJobOffers";
import { Link } from "react-router-dom";
import { useProduct } from "../hooks/core/useProduct";

export default function DashboardPage() {
  const { selectedProduct } = useProduct();
  const { posts } = usePosts();
  const { reviews } = useReviews();
  const { faqs } = useFaqs();
  const { jobOffers } = useJobOffers();

  // ✅ Filtrer par produit sélectionné
  const filteredPosts = posts.filter((p) => {
    if (selectedProduct === 'all') return true;
    return p.product_name === selectedProduct;
  });

  const filteredReviews = reviews.filter((r) => {
    if (selectedProduct === 'all') return true;
    return r.product_name === selectedProduct;
  });

  const filteredFaqs = faqs.filter((f) => {
    if (selectedProduct === 'all') return true;
    return f.product_name === selectedProduct;
  });

  const filteredJobOffers = jobOffers.filter((j) => {
    if (selectedProduct === 'all') return true;
    return j.product_name === selectedProduct;
  });

  const stats = [
    {
      title: "Articles",
      value: filteredPosts.length,
      icon: FileText,
      color: "bg-blue-500",
      link: "/admin/blog",
      published: filteredPosts.filter((p) => p.status === "published").length,
    },
    {
      title: "Avis",
      value: filteredReviews.length,
      icon: Star,
      color: "bg-yellow-500",
      link: "/admin/reviews",
      visible: filteredReviews.filter((r) => r.is_visible).length,
    },
    {
      title: "FAQ",
      value: filteredFaqs.length,
      icon: HelpCircle,
      color: "bg-purple-500",
      link: "/admin/faq",
      visible: filteredFaqs.filter((f) => f.is_visible).length,
    },
    {
      title: "Offres d'emploi",
      value: filteredJobOffers.length,
      icon: Briefcase,
      color: "bg-green-500",
      link: "/admin/job-offers",
      visible: filteredJobOffers.filter((j) => j.is_visible).length,
    },
  ];

  const recentPosts = filteredPosts.slice(0, 5);
  const recentReviews = filteredReviews.slice(0, 5);

  return (
    <div className="p-6">
      {/* Bienvenue */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="text-gray-500 mt-1">
          Bienvenue sur votre espace d'administration
          {selectedProduct !== 'all' && (
            <span className="ml-2 text-sm text-primary font-medium">
              • Filtré par : {selectedProduct}
            </span>
          )}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <Link
            key={index}
            to={stat.link}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${stat.color} bg-opacity-10`}>
                <stat.icon className={`w-6 h-6 ${stat.color.replace("bg-", "text-")}`} />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
            </div>
            <h3 className="text-gray-600 font-medium">{stat.title}</h3>
            {"published" in stat && <p className="text-sm text-gray-400 mt-1">{stat.published} publié(s)</p>}
            {"visible" in stat && <p className="text-sm text-gray-400 mt-1">{stat.visible} visible(s)</p>}
          </Link>
        ))}
      </div>

      {/* Derniers articles et avis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Derniers articles */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Derniers articles</h2>
              <Link to="/admin/blog" className="text-sm text-primary hover:underline">
                Voir tout
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentPosts.length > 0 ? (
              recentPosts.map((post) => (
                <div key={post.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800">{post.title}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(post.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        post.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {post.status === "published" ? "Publié" : "Brouillon"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-gray-500">Aucun article pour le moment</div>
            )}
          </div>
        </div>

        {/* Derniers avis */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Derniers avis</h2>
              <Link to="/admin/reviews" className="text-sm text-primary hover:underline">
                Voir tout
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentReviews.length > 0 ? (
              recentReviews.map((review) => (
                <div key={review.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    {review.user_image ? (
                      <img
                        src={review.user_image}
                        alt={review.user_name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <Users className="w-5 h-5 text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-800">{review.user_name}</p>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={14}
                              className={i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-1 mt-1">{review.comment}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(review.original_date).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-gray-500">Aucun avis pour le moment</div>
            )}
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Link
            to="/admin/blog/posts/new"
            className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:shadow-md transition-shadow"
          >
            <FileText className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">Nouvel article</p>
          </Link>
          <Link
            to="/admin/reviews/new"
            className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:shadow-md transition-shadow"
          >
            <Star className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">Nouvel avis</p>
          </Link>
          <Link
            to="/admin/faq/new"
            className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:shadow-md transition-shadow"
          >
            <HelpCircle className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">Nouvelle FAQ</p>
          </Link>
          <Link
            to="/admin/job-offers/new"
            className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:shadow-md transition-shadow"
          >
            <Briefcase className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">Nouvelle offre</p>
          </Link>
        </div>
      </div>
    </div>
  );
}